import type { MessageSummary } from '@/types';

/** Temporary navigation intent; never a persisted preference or an ACL. */
export function explicitConversationThread(pathname: string, params: URLSearchParams): number | null {
  if (!/^\/(messages|tickets)(?:\/|$)/.test(pathname) || params.getAll('thread').length !== 1) return null;
  const raw = params.get('thread') ?? '';
  if (!/^[1-9]\d*$/.test(raw)) return null;
  const value = Number(raw);
  return Number.isSafeInteger(value) ? value : null;
}

export function writeConversationIntent(params: URLSearchParams, threadId: number | null): URLSearchParams {
  const next = new URLSearchParams(params);
  if (threadId == null) next.delete('thread');
  else {
    if (!Number.isSafeInteger(threadId) || threadId <= 0) return next;
    next.set('thread', String(threadId));
    ['historyAuthorId', 'historySource', 'authorId', 'pipeline'].forEach((key) => next.delete(key));
  }
  return next;
}

export function conversationRequestOverrides(threadId: number | null): {
  thread_ref_message_id?: number; author_id?: undefined; author_query?: undefined;
  source_channel_id?: undefined; source_instance?: undefined;
} {
  return threadId == null ? {} : { thread_ref_message_id: threadId, author_id: undefined,
    author_query: undefined, source_channel_id: undefined, source_instance: undefined };
}

/** A bounded read of the authenticated graph, independent of the visible feed page. */
export async function loadConversationPages(
  threadId: number,
  read: (options: { thread_ref_message_id: number; limit: number; cursor?: number; cursor_ts?: string }) => Promise<MessageSummary[]>,
  isCurrent: () => boolean,
): Promise<{ items: MessageSummary[]; complete: boolean }> {
  if (!Number.isSafeInteger(threadId) || threadId <= 0) throw new Error('conversation_invalid_seed');
  const rows = new Map<number, MessageSummary>();
  let cursor: number | undefined;
  let cursorTs: string | undefined;
  let complete = false;
  for (let page = 0; page < 5; page += 1) {
    if (!isCurrent()) throw new Error('conversation_superseded');
    const batch = await read({ thread_ref_message_id: threadId, limit: 100, cursor, cursor_ts: cursorTs });
    if (!isCurrent()) throw new Error('conversation_superseded');
    if (!Array.isArray(batch) || batch.length > 100) throw new Error('conversation_invalid_page');
    for (const row of batch) {
      if (!Number.isSafeInteger(row.id) || row.id <= 0) throw new Error('conversation_invalid_ref');
      const old = rows.get(row.id);
      if (old && (old.source_created_at !== row.source_created_at || old.message_uid !== row.message_uid || old.tenant_id !== row.tenant_id)) {
        throw new Error('conversation_ambiguous_ref');
      }
      rows.set(row.id, row);
    }
    if (batch.length < 100) { complete = true; break; }
    const tail = batch[batch.length - 1];
    const nextTs = tail.source_created_at ?? tail.created_at ?? undefined;
    if (!nextTs || (cursor === tail.id && cursorTs === nextTs)) break;
    cursor = tail.id;
    cursorTs = nextTs;
  }
  return { items: Array.from(rows.values()).sort((a, b) => {
    const time = Date.parse(a.source_created_at ?? a.created_at ?? '') - Date.parse(b.source_created_at ?? b.created_at ?? '');
    return (Number.isFinite(time) && time !== 0) ? time : a.id - b.id;
  }), complete };
}
